'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  login,
  logout,
  registerTenant,
  forgotPassword,
  resetPassword,
  type ForgotPasswordPayload,
  type ResetPasswordPayload,
  type MessageResponse,
} from '@/services/authService'
import { LoginCredentials, AuthResponse } from '@/types/UserType'
import { RegisterTenantPayload } from '@/types/RegisterTenantType'
import {
  setAccessToken,
  removeAccessToken,
  setSelectedTenantId,
  removeSelectedTenantId,
  setSelectedTenantRole,
  removeSelectedTenantRole,
} from '@/services/storage'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/providers/auth-provider'
import { removeRegisterDraft } from '@/features/auth/register-draft'
import { toast } from 'sonner'
export function useLoginMutation() {
  const queryClient = useQueryClient()
  const router = useRouter()
  const { setAuthUser, setLicenseBlocked } = useAuth()

  return useMutation<AuthResponse, unknown, LoginCredentials>({
    mutationFn: login,
    onSuccess: (data) => {
      setAccessToken(data.token)
      setAuthUser(data.user)
      const tenant = data.tenants?.[0]
      if (tenant) {
        setSelectedTenantId(tenant.tenant_uuid)
        setSelectedTenantRole(tenant.role)
      }
      queryClient.setQueryData(['user'], data.user)

      if (tenant?.license?.blocked) {
        setLicenseBlocked(tenant.license.message)
        router.push('/acesso-bloqueado')
        return
      }

      router.push('/dashboard')
    },
  })
}

export function useRegisterTenantMutation() {
  const queryClient = useQueryClient()
  const router = useRouter()
  const { setAuthUser } = useAuth()

  return useMutation<AuthResponse, unknown, RegisterTenantPayload>({
    mutationFn: registerTenant,
    onSuccess: (data) => {
      removeRegisterDraft()
      setAccessToken(data.token)
      setAuthUser(data.user)
      if (data.tenants && data.tenants.length > 0) {
        setSelectedTenantId(data.tenants[0].tenant_uuid)
        setSelectedTenantRole(data.tenants[0].role)
      }
      queryClient.setQueryData(['user'], data.user)
      router.push('/dashboard')
      toast.success('Cadastro realizado com sucesso!')
    },
    onError: () => {
      toast.error('Não foi possível realizar o cadastro.')
    },
  })
}

export function useForgotPasswordMutation() {
  return useMutation<MessageResponse, unknown, ForgotPasswordPayload>({
    mutationFn: forgotPassword,
  })
}

export function useResetPasswordMutation() {
  const router = useRouter()

  return useMutation<MessageResponse, unknown, ResetPasswordPayload>({
    mutationFn: resetPassword,
    onSuccess: (data) => {
      toast.success(data.message || 'Senha redefinida com sucesso!')
      router.push('/login')
    },
  })
}

export function useLogoutMutation() {
  const queryClient = useQueryClient()
  const router = useRouter()
  const { clearAuth } = useAuth()

  return useMutation<void, unknown, void>({
    mutationFn: logout,
    onSettled: () => {
      removeAccessToken()
      removeSelectedTenantId()
      removeSelectedTenantRole()
      clearAuth()
      queryClient.removeQueries({ queryKey: ['user'], exact: true })
      router.push('/login')
    },
  })
}

